import axios from 'axios';
import { NO_PERMISSION_MESSAGE } from '../hooks/useMenuPermission';
import useModalStore from '../zustand/store/useModalStore';

let isLoginOut = false;

axios.defaults.withCredentials = true;

// 서버가 401과 함께 내려주는 코드 중, 로그인 페이지로 보내야 하는 코드 목록
// - DUPLICATE_LOGIN : 다른 기기에서 로그인 (JwtAuthFilter)
// - SESSION_EXPIRED : 세션 만료 (AuthInterceptor)
// - OTP_REQUIRED    : OTP 인증 미완료 세션 (AuthInterceptor)
const AUTH_ERROR_CODES = ['DUPLICATE_LOGIN', 'SESSION_EXPIRED', 'OTP_REQUIRED'];

// 인증이 끊겨 로그인 화면으로 나가는 중인지. SessionCheck 가 중복 알림을 피하려고 본다.
export const isAuthExiting = () => isLoginOut;

// 다시 로그인에 성공하면 Login 이 호출한다.
// 로그인 화면으로는 SPA 라우팅으로 이동하므로 페이지가 새로 로드되지 않아 isLoginOut 이 저절로 풀리지 않는다.
// 풀지 않으면 그 탭에서는 SessionCheck 가 계속 건너뛰고, 다음 401 도 로그인 화면으로 보내지 못한다.
export const resetAuthExit = () => { isLoginOut = false; };

// 라우터의 navigate 를 훅 밖에서 쓰기 위한 창구. Layout 이 마운트될 때 등록한다.
// 이걸 쓰면 화면 전환이 SPA 라우팅으로 즉시 일어나 번들을 다시 내려받지 않는다.
let spaNavigate = null;
export const registerAuthExitNavigator = (fn) => { spaNavigate = fn; };

// 접속로그(2단계)용. 모든 요청에 현재 화면 경로를 실어 보내면
// 서버가 메뉴를 특정할 수 있어, 로깅 정책에 메뉴를 일일이 매핑하지 않아도 된다.
// 새 메뉴가 생겨도 라우트만 있으면 로그에 메뉴가 자동으로 붙는다.
axios.interceptors.request.use(config => {
    try {
        config.headers['X-Menu-Sorc'] = window.location.pathname;
    } catch (e) {
        // 헤더를 못 붙여도 요청은 그대로 진행한다. 로그에 메뉴가 비는 것뿐이다.
    }
    return config;
});

// 로그인 화면으로 내보낸다. 401 처리와, 서버가 이미 로그아웃시킨 경우(본인 탈퇴 · 비밀번호 5회 오류 잠금)가 함께 쓴다.
// 동시에 여러 번 불려도 알림·이동은 1회만 한다.
export const exitToLogin = (message) => {
    if (isLoginOut) {
        return;
    }
    isLoginOut = true;

    // 인증 정보는 즉시 정리한다. SessionCheck 는 isAuthExiting() 으로 막혀 있어
    // 여기서 지워도 "로그인 정보가 없습니다" 모달이 따라 뜨지 않는다.
    Object.keys(window.localStorage).forEach((key) => {
        if (!key.startsWith('pophide_')) {
            window.localStorage.removeItem(key);
        }
    });

    // 무효가 된 토큰도 버린다. SPA 이동이라 페이지가 다시 로드되지 않으므로 직접 지워야 한다.
    // 남겨 두면 이후 요청마다 옛 토큰이 실려 서버가 계속 중복로그인으로 판단한다.
    delete axios.defaults.headers.common['Authorization'];

    // 마이페이지 창도 닫는다. 열림 상태가 화면 메모리(store)에 남아 있어, 닫지 않으면 다시 로그인했을 때 열린 채로 나온다
    // (마이페이지 안의 비밀번호 변경 · 탈퇴에서 잠기거나 세션이 만료된 경우)
    useModalStore.getState().setMypageModal(false);

    // replace : 뒤로가기로 만료된 화면에 되돌아가지 않게 한다
    const hardExit = () => window.location.replace('/member/login.do');

    // 화면 전환과 알림을 같이 한다.
    // 확인을 누른 뒤에 이동시키면 그동안 만료된 화면이 그대로 보인다.
    // SPA 라우팅으로 옮기면 번들을 다시 내려받지 않아 즉시 전환되고,
    // Layout 이 Router 를 감싸고 있어 모달도 유지된다.
    if (spaNavigate) {
        try {
            spaNavigate('/member/login.do', { replace: true });
            useModalStore.getState().showNotiModal(message);
            // 안전망 : SPA 이동이 먹지 않았으면 전체 리로드로 확실히 내보낸다.
            // 만료된 화면에 남아 있는 것이 가장 나쁜 결과다.
            window.setTimeout(() => {
                if (window.location.pathname !== '/member/login.do') {
                    hardExit();
                }
            }, 500);
        } catch (e) {
            hardExit();
        }
    } else {
        // Layout 이 아직 마운트되지 않은 초기 로딩 단계
        hardExit();
    }
};

// 권한 없음(403) 안내를 한 화면에서 여러 번 띄우지 않기 위한 표시
let forbiddenShown = false;

axios.interceptors.response.use(
    response => response,
    error => {
        if (
            error.response?.status === 401 &&
            AUTH_ERROR_CODES.includes(error.response.data?.error) &&
            !isLoginOut
        ) {
            exitToLogin(error.response.data?.message
                || '세션이 만료되었습니다.\n다시 로그인해주세요.');
        } else if (error.response?.status === 403 && !forbiddenShown) {
            // 권한이 없는 기능이다. 로그아웃시키지도 화면을 옮기지도 않고 안내만 한다.
            // 서버 응답이 {"error":"FORBIDDEN","message"} 면 그 문구를, 아니면(스프링 기본 오류 응답) 기본 문구를 쓴다.
            const data = error.response.data;
            const message = (data?.error === 'FORBIDDEN' && data?.message)
                || NO_PERMISSION_MESSAGE;
            forbiddenShown = true;
            // 화면별 catch 의 안내가 같은 모달을 덮어쓰지 않도록 catch 가 끝난 뒤에 띄운다
            window.setTimeout(() => {
                useModalStore.getState().showNotiModal(message);
                window.setTimeout(() => { forbiddenShown = false; }, 1000);
            }, 0);
        }
        return Promise.reject(error);
    }
);

